import type { PlacesContainer } from '@elara/client-core/containers/PlacesContainer';
import type { Place } from '@elara/client-core/models/PlaceModel';

import type { ToolResultRowEnricher } from './types.js';

import { faviconUrl } from '@elara/client-core/chat/tool-badges/extractors';

import { useRefreshPlace } from '#lib/use-refresh-place.js';
import { useApp } from '#states/hooks.js';

import { FaviconImage } from '../FaviconImage.js';

function useLoadedPlace(placeId: string | undefined): Place | undefined {
  const { features } = useApp('features');
  const places = (features as unknown as { places?: PlacesContainer }).places;
  useRefreshPlace(placeId);
  const handle = placeId && places ? places.ClientPlace.get({ id: placeId }) : undefined;
  if (!handle?.isReady || handle.error) return undefined;
  return handle.data;
}

/**
 * Place rows use the Firestore-loaded place's `shortAddress` (line1, city, country)
 * rather than the raw Google `formatted_address` string on the tool output, which is
 * ordered in the native country's convention (e.g. German address order). When the
 * place has a `website`, we render its favicon as the leading icon; if the favicon
 * 404s (or the place has no website), we fall back to the category Material icon
 * that the extractor selected from the Google `primaryType`.
 */
export const placeRowEnricher: ToolResultRowEnricher<Place> = {
  useModel: useLoadedPlace,
  resolveTitle: ({ model, item }) => model?.name ?? item.title,
  resolveSubtitle: ({ model, item }) => model?.shortAddress ?? model?.formattedAddress ?? item.subtitle,
  renderIcon: ({ model, item, toolIcon }) => {
    const website = model?.website ?? undefined;
    const favicon = website ? faviconUrl(website) : undefined;
    if (!favicon) return undefined;
    return <FaviconImage src={favicon} fallbackIcon={item.iconName ?? toolIcon} />;
  },
};
